import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Pagination } from '../components/Pagination';
import { discountPct, formatDateTime, formatMoney, relativeTime } from '../lib/format';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { useUrlFilters } from '../lib/useUrlFilters';

type SortOption = 'newest' | 'price_asc' | 'price_desc' | 'discount';

const PAGE_SIZE = 60;

type TextFilters = { search: string; brand: string; minPrice: string; maxPrice: string };

export function Products() {
  const [params, setParams] = useUrlFilters();

  // Selects y página: la URL es la fuente de verdad directa.
  const store = params.get('store') ?? '';
  const sort = (params.get('sort') ?? 'newest') as SortOption;
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  // Inputs de texto: draft local + debounce, y recién ahí se escribe a la URL
  // (con replace, para no crear una entrada de historial por tecla).
  const [draft, setDraft] = useState<TextFilters>({
    search: params.get('search') ?? '',
    brand: params.get('brand') ?? '',
    minPrice: params.get('minPrice') ?? '',
    maxPrice: params.get('maxPrice') ?? '',
  });
  const debounced = useDebouncedValue(draft, 300);

  useEffect(() => {
    setParams(debounced, { replace: true, resetPage: true });
  }, [debounced, setParams]);

  // Si la URL cambia por navegación (back/forward), realinear el draft.
  const urlSearch = params.get('search') ?? '';
  const urlBrand = params.get('brand') ?? '';
  const urlMinPrice = params.get('minPrice') ?? '';
  const urlMaxPrice = params.get('maxPrice') ?? '';
  useEffect(() => {
    if (
      urlSearch !== debounced.search ||
      urlBrand !== debounced.brand ||
      urlMinPrice !== debounced.minPrice ||
      urlMaxPrice !== debounced.maxPrice
    ) {
      setDraft({ search: urlSearch, brand: urlBrand, minPrice: urlMinPrice, maxPrice: urlMaxPrice });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearch, urlBrand, urlMinPrice, urlMaxPrice]);

  const stores = useQuery({ queryKey: ['stores'], queryFn: () => api.stores() });
  const products = useQuery({
    queryKey: ['products', { search: urlSearch, store, brand: urlBrand, minPrice: urlMinPrice, maxPrice: urlMaxPrice, sort, page }],
    queryFn: () => api.products({
      search:   urlSearch   || undefined,
      store:    store       || undefined,
      brand:    urlBrand    || undefined,
      minPrice: urlMinPrice ? Number(urlMinPrice) : undefined,
      maxPrice: urlMaxPrice ? Number(urlMaxPrice) : undefined,
      sort,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    placeholderData: (prev) => prev,
  });

  const setTextFilter = (key: keyof TextFilters) => (value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));

  return (
    <>
      <div className="toolbar">
        <input
          type="search"
          placeholder="Buscar productos…"
          value={draft.search}
          onChange={(e) => setTextFilter('search')(e.target.value)}
        />
        <select
          value={store}
          onChange={(e) => setParams({ store: e.target.value }, { resetPage: true })}
        >
          <option value="">Todas las tiendas</option>
          {stores.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) =>
            setParams({ sort: e.target.value === 'newest' ? '' : e.target.value }, { resetPage: true })
          }
        >
          <option value="newest">Más recientes</option>
          <option value="price_asc">Precio: menor a mayor</option>
          <option value="price_desc">Precio: mayor a menor</option>
          <option value="discount">Mayor descuento</option>
        </select>
      </div>
      <div className="toolbar">
        <input
          type="text"
          placeholder="Marca (ej. adidas, Nike…)"
          value={draft.brand}
          onChange={(e) => setTextFilter('brand')(e.target.value)}
          style={{ maxWidth: 180 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          S/
          <input
            type="number"
            placeholder="Mín"
            value={draft.minPrice}
            min={0}
            onChange={(e) => setTextFilter('minPrice')(e.target.value)}
            style={{ width: 80 }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          —
          <input
            type="number"
            placeholder="Máx"
            value={draft.maxPrice}
            min={0}
            onChange={(e) => setTextFilter('maxPrice')(e.target.value)}
            style={{ width: 80 }}
          />
        </label>
        {(draft.brand || draft.minPrice || draft.maxPrice) && (
          <button
            style={{ fontSize: 12, padding: '2px 8px', cursor: 'pointer' }}
            onClick={() => setDraft((d) => ({ ...d, brand: '', minPrice: '', maxPrice: '' }))}
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {products.isLoading && <div className="loading">Cargando…</div>}
      {products.isError && <div className="error">Error: {(products.error as Error).message}</div>}
      {products.data && products.data.items.length === 0 && (
        <div className="empty">Sin resultados.</div>
      )}

      <div className="grid">
        {products.data?.items.map((p) => {
          const pct = discountPct(p.current_price_cents, p.list_price_cents);
          return (
            <Link key={p.id} to={`/p/${p.id}`} className="card">
              {p.image_url ? (
                <img className="card-img" src={p.image_url} alt="" loading="lazy" />
              ) : (
                <div className="card-img-fallback">sin imagen</div>
              )}
              <div className="card-name" title={p.name}>
                {p.name}
              </div>
              <div className="card-price-row">
                <span className="card-price">{formatMoney(p.current_price_cents)}</span>
                {p.list_price_cents && p.list_price_cents !== p.current_price_cents && (
                  <span className="card-list">{formatMoney(p.list_price_cents)}</span>
                )}
                {pct != null && <span className="badge badge-discount">-{pct}%</span>}
              </div>
              <div className="card-foot">
                <span>{p.store_id}</span>
                {p.brand && <span>{p.brand}</span>}
              </div>
              <div
                className="card-seen"
                title={p.priced_at ? formatDateTime(p.priced_at) : 'sin escaneos'}
              >
                Visto {relativeTime(p.priced_at)}
              </div>
            </Link>
          );
        })}
      </div>

      {products.data && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={products.data.total}
          onChange={(p) => {
            setParams({ page: p === 1 ? '' : String(p) });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      )}
    </>
  );
}
